import { useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';

import Navbar from './NavBar';
import { HeaderProps } from '../../../../shared/types/header';
import CloseIcon from '../../../../assets/icons/General/close.svg?react';
import ChevronIcon from '../../../../assets/icons/General/chevron.svg?react';
import IntellectPlainLogo from '../../../../assets/IntellectPlainLogo';
import { AnimatePresence, motion } from 'motion/react';
import { useDeviceTypeV2 } from '../../../../utilities/common/deviceTypeV2';
import intellectLogoText from '../../../../assets/intellect-logo-text.svg';
import IntellectLogo from '../../../../assets/intellect-logo.svg';

function SideBar({
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarCollapsed = false,
  setIsSidebarCollapsed,
  toggleSidebarCollapsed,
}: HeaderProps) {
  const isMobile = useDeviceTypeV2();

  useLayoutEffect(() => {
    if (isMobile) {
      setIsSidebarOpen && setIsSidebarOpen(false);
      // Reset collapsed state on mobile
      if (setIsSidebarCollapsed) {
        setIsSidebarCollapsed(false);
      }
    }
  }, [isMobile, setIsSidebarOpen, setIsSidebarCollapsed]);

  return (
    <aside
      className={`bg-taupe-400 border-taupe-500 fixed top-0 left-0 z-50 h-dvh transform justify-between border-r transition-all duration-300 ease-in-out md:top-0 md:pb-0 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } flex flex-col md:translate-x-0 ${
        isSidebarCollapsed ? 'w-22 px-4' : 'w-60 px-4'
      } transform-gpu hover:shadow-lg md:pt-6`}
    >
      {/* Toggle Button - Desktop Only */}
      {!isMobile && (
        <button
          onClick={toggleSidebarCollapsed}
          className="hover:bg-taupe-400 border-taupe-500 pointer-events-auto absolute top-8 -right-3 z-[60] flex h-6 w-6 items-center justify-center rounded-full border bg-white shadow-md transition-all duration-200 hover:shadow-lg focus:outline-none"
          title={`${isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} (Ctrl+B)`}
          aria-label={`${isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} (Ctrl+B)`}
          aria-expanded={!isSidebarCollapsed}
        >
          <ChevronIcon
            className={`stroke-taupe-700 h-3 w-3 transition-transform duration-300 ease-in-out ${
              isSidebarCollapsed ? 'rotate-270' : 'rotate-90'
            }`}
          />
        </button>
      )}

      {/* Mobile Header - Outside scrollable area so sticky works */}
      <div className="sticky top-4 mb-[33px] flex items-center justify-between md:hidden">
        <Link
          to="/d/dimensions"
          onClick={() => setIsSidebarOpen && setIsSidebarOpen(false)}
        ></Link>
        <button className="hover:bg-black-100 active:bg-black-200 mr-[18px] rounded-lg p-2 outline-hidden transition-all duration-150">
          <CloseIcon
            className="stroke-black-900 w-3 cursor-pointer"
            onClick={() => setIsSidebarOpen && setIsSidebarOpen(false)}
          />
        </button>
      </div>

      <div className="flex-1 overflow-x-hidden overflow-y-auto pb-12 [-ms-overflow-style:none] [scrollbar-width:none] md:pb-0 [&::-webkit-scrollbar]:hidden">
        <div
          aria-hidden
          className="relative mb-6 flex flex-row items-center justify-start gap-3 pl-4"
        >
          <motion.div
            initial={{ rotateZ: 90 }}
            animate={isSidebarCollapsed ? { rotateZ: 0 } : { rotateZ: 360 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <img src={IntellectLogo} alt="Intellect Logo" />
          </motion.div>
          <AnimatePresence>
            {!isSidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0, transition: { delay: 0.2 } }}
                exit={{ opacity: 0, x: -5, transition: { duration: 0.1 } }}
              >
                <img
                  src={intellectLogoText}
                  alt="Intellect Logo Text"
                  aria-hidden="true"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Navbar
          setIsSidebarOpen={setIsSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      </div>
      <motion.div
        aria-hidden
        initial={{ opacity: 0, right: isSidebarCollapsed ? 300 : 20 }}
        animate={{
          opacity: 0.5,
          right: isSidebarCollapsed ? 300 : 20,
          transition: {
            ease: 'easeInOut',
          },
        }}
        className={`absolute bottom-64 ${isSidebarCollapsed ? 'hidden' : ''}`}
      >
        <IntellectPlainLogo className="text-taupe-500" width={80} height={80} />
      </motion.div>
      <motion.div
        aria-hidden
        initial={{ opacity: 0, left: isSidebarCollapsed ? -300 : -75 }}
        animate={{
          opacity: 0.5,
          left: isSidebarCollapsed ? -300 : -75,
          transition: {
            ease: 'easeInOut',
          },
        }}
        className={`absolute -bottom-10 ${isSidebarCollapsed ? 'hidden' : ''}`}
      >
        <IntellectPlainLogo
          className="text-taupe-500"
          width={300}
          height={300}
        />
      </motion.div>
    </aside>
  );
}

export default SideBar;
